import React, { useEffect, useRef, useState } from 'react';
import { Loader } from 'lucide-react';
import Spacing from '../Spacing';
import { Config } from '../../Config';
import { notify, userAccessToken } from '../../helpers';
import { Drawer } from 'vaul';

export default function DocflowRecordingModal(props) {
  const {
    activeModal = false,
    setActiveModal = () => {},
    setTranscribedText = () => {},
    previousScrollPosition = 0,
  } = props;

  const [enableTranscribe, setEnableTranscribe] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [animationClass, setAnimationClass] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    startRecording();
  }, []);

  // Start Recording Audio
  const startRecording = async () => {
    try {
      // Check microphone permission
      const permissionStatus = await navigator.permissions.query({
        name: "microphone",
      });

      if (permissionStatus.state === "denied") {
        notify("error", "Microphone access is denied. Please enable it in your browser settings.", 0.5);
        return;
      }

      // If permission is granted or prompt, start recording
      setIsRecording(true);
      setAnimationClass("speaking"); // Start animation

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      const audioChunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl); // Set the recorded audio URL
        setRecordedAudio(audioBlob); // Save the recorded audio Blob
      };

      mediaRecorderRef.current.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      notify("error", "Failed to access microphone. Please check your browser settings.", 0.5);
    }
  };

  // Stop Recording Audio
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    setAnimationClass(""); // Stop animation
  };

  // Handle Transcription (sending audio for transcription)
  const handleTranscribe = async () => {
    if (!recordedAudio) {
      notify("error", "Please record audio first.", 0.5);
      return;
    }
    setEnableTranscribe(true);
    // Prepare the recorded audio as a formData object
    const formData = new FormData();
    formData.append("file", recordedAudio, "audio.wav");

    try {
      const response = await fetch(`${Config.notificationServerUrl}/voice/transcribe-audio`, {
        method: "POST",
        headers: {
          // "Content-Type": "multipart/form-data",
          // accept: "application/json",
          Authorization: `Bearer ${userAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Transcription failed");
      }

      const data = await response.json();

      // Append new transcription instead of replacing previous text
      setTranscribedText((prevText) => prevText ? prevText + "\n" + data.transcription : data.transcription);
      handleDrawerClose();
    } catch (error) {
      console.error("Error transcribing audio:", error);
      notify("error", "Error transcribing audio. Please try again", 0.5);
    } finally {
      setEnableTranscribe(false);
    }
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 small">
            <Drawer.Title className="sr-only">Delete Record</Drawer.Title>
            <Drawer.Description className="sr-only">Delete a recorded data</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_2 docpro_style_auto">
              <div className="docpro_modal">
                <h2 className="docpro_fs_24 docpro_mb_0">Voice Transcription</h2>
                {audioUrl && !isRecording ?
                  <Spacing lg="20" sm="20" />
                :
                  <Spacing lg="40" sm="40" />
                }
                <div className='text-center'>
                  {animationClass &&
                    <>
                      <label className="docpro_fw_600 docpro_primary_color">RECORDING ...</label>
                      <div className={`animation ${animationClass}`}></div>
                    </>
                  }
                  <button
                    className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn ${isRecording ? "stop" : "start"}`}
                    onClick={isRecording ? stopRecording : startRecording}
                    style={{ padding: '12px 30px', borderRadius: '20px' }}
                  >
                    {isRecording ? "Stop Recording" : (audioUrl && !isRecording) ? "Restart Record" : "Start Record"}
                  </button>

                  {!animationClass && (
                    <Spacing lg="25" sm="25" />
                  )}

                  {audioUrl && !isRecording && (
                    <div>
                      <audio ref={audioPlayerRef} controls src={audioUrl}></audio>
                      <br /><br />
                      <div className='flex items-center' style={{ justifyContent: "center" }}>
                        <button
                          className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn"
                          onClick={handleTranscribe}
                          style={{ padding: '12px 30px', borderRadius: '20px' }}
                          disabled={enableTranscribe ? true : false}
                        >
                          Transcribe
                          {enableTranscribe && (
                            <span className="docpro_loading">
                              <Loader width={20} height={20} />
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  <br />
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
